import { ArraySchema, ISchema, Lazy, ObjectSchema, Reference } from 'yup';

export interface ISchemaNode {
  schema: ISchema<any>;
  path: undefined | string;
  key?: string | number;
  /**
   * Indica se si trova all'interno di un array, e qual 'è il path di questo
   */
  inArrayPath?: string;
  inArrayIndex?: number;
  /**
   * Lista di tutti i path degli array genitori
   */
  inArrayParents?: string[];
  value: any;
  parent?: any;
  from?: any[];
}

export interface IForEachSchemaOptions {
  value: any;
  context?: any;
  moduleRequestId?: string;
}

/**
 * Versione ottimizzata di `traverseSchema` che non applica il `describe` per
 * ogni nodo che visita, riducendo il costo computazionale.
 * Inoltre utilizza una coda FIFO per evitare di ricorrere alla ricorsione
 * e alle operazioni low-level di iterazione (`for`, `for in`)
 */
export function forEachSchemaNode(
  initialSchema: ISchema<any>,
  options: IForEachSchemaOptions,
  cb: (node: ISchemaNode) => boolean | null | undefined | void,
) {
  const queue: ISchemaNode[] = [
    { schema: initialSchema, path: undefined, value: options.value },
  ];
  while (queue.length) {
    const node = queue.shift()!;
    const schema = node.schema.resolve({ ...node, context: options.context });
    const shouldBreakEarly = cb({ ...node, schema });
    if (shouldBreakEarly === false) return;

    if (schema instanceof ArraySchema) {
      if (!Array.isArray(node.value) || !schema.innerType) {
        continue;
      }

      const valuesLen = node.value.length;
      for (let i = 0; i < valuesLen; i++) {
        const value = node.value[i];
        const path = node.path ? `${node.path}.${i}` : `${i}`;
        queue.push({
          schema: schema.innerType,
          key: i,
          inArrayPath: node.path,
          inArrayIndex: i,
          inArrayParents: [
            ...(node.inArrayParents ?? []),
            ...(node.path ? [path] : []),
          ],
          path,
          value,
          // È necessario che il parent non sia l'array ma l'oggetto padre, per
          // via di come gestiamo il `parentWhen`: `^.a` deve fare riferimento
          // all'oggetto padre e non all'array in se.
          //
          // Se volessimo cambiare questo comportamento, dobbiamo predisporre o
          // una sintassi diversa, ma sicuramente dovremmo allineare anche
          // `yup` per gestire correttamente la risoluzione del `from`.
          from: [...(node.from ?? [])],
        });
      }
    }

    if (schema instanceof ObjectSchema) {
      for (const key in schema.fields) {
        if (Reference.isRef(schema.fields[key])) continue;

        const value = node.value?.[key];
        const path = node.path ? `${node.path}.${key}` : `${key}`;
        queue.push({
          schema: schema.fields[key] as ISchema<any>,
          inArrayPath: node.inArrayPath,
          inArrayIndex: node.inArrayIndex,
          inArrayParents: node.inArrayParents,
          key,
          path,
          value,
          parent: node.value,
          from: [{ schema, value: node.value }, ...(node.from ?? [])],
        });
      }
    }

    if (schema instanceof Lazy) {
      queue.push({ ...node, schema: schema.resolve(options) });
    }
  }
}
